
const libPictView = require('pict-view');
const libNavigationTopics = require('../navigation-topics.js');

const html = String.raw;

/**
 * Host view — the Configurations "home" screen and
 * the app's landing page. Renders:
 *   1. a KPI jumbotron (all platform counts as small wide rectangles, Customers gated);
 *   2. the navigation-style chooser (Sidebar / Hub / List / Command Palette);
 *   3. the full navigation (everything) — page styles render it here, the Sidebar style
 *      docks it as a rail (handled by the application) and this screen shows a welcome.
 */
class ConfigurationNavigation extends libPictView
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);

		/** @type {any} */
		this.pict;
	}

	get navigationProvider()
	{
		return this.pict.providers['Pict-Navigation'];
	}

	onBeforeRender(pRenderable)
	{
		// Jumbotron data (read by the template); KPI values come from the live counts.
		const tmpTopic = libNavigationTopics.TOPIC_CONFIGS.Settings;
		this.pict.AppData.NavJumbo = { Title: tmpTopic.Title, Icon: tmpTopic.Icon, Blurb: tmpTopic.Blurb };
		this.pict.AppData.NavKpis = libNavigationTopics.buildKpiRecords(
			this.pict.AppData.DashboardStats, tmpTopic.KpiKeys, this.pict.PictApplication.isHeadlightSession());
		return super.onBeforeRender(pRenderable);
	}

	onAfterRender(pRenderable)
	{
		this._paintChooser();
		this.show(this.pict.PictApplication.navigationStyle);
		this.pict.CSSMap.injectCSS();
		return super.onAfterRender(pRenderable);
	}

	/** Build the style chooser buttons from the provider's render-type metadata. */
	_paintChooser()
	{
		const tmpProvider = this.navigationProvider;
		if (!tmpProvider) { return; }
		let tmpButtons = '';
		tmpProvider.getRenderTypes().forEach((pType) =>
		{
			tmpButtons += `<button id="ConfigNavSwitch-${pType.Key}" type="button" class="button is-small" title="${pType.Blurb || ''}" onclick="_Pict.views.ConfigurationNavigation.show('${pType.Key}')">`
				+ `<span class="icon is-small">${this.pict.icon(pType.Icon || 'Menu')}</span><span>${pType.Label}</span></button>`;
		});
		this.pict.ContentAssignment.assignContent('#ConfigNav-Switcher',
			`<span class="config-nav-switcher-label">Navigation style</span><div class="buttons has-addons">${tmpButtons}</div>`);
	}

	/**
	 * Apply + render a navigation style. Persists/reshapes the shell only when the style
	 * actually changes (the shell was already applied at login), then paints this screen.
	 *
	 * @param {string} pStyle - 'Sidebar' | 'Hub' | 'List' | 'Palette'
	 */
	show(pStyle)
	{
		const tmpApp = this.pict.PictApplication;
		const tmpProvider = this.navigationProvider;
		if (pStyle !== tmpApp.navigationStyle)
		{
			tmpApp.setNavigationStyle(pStyle);
		}

		tmpProvider.getRenderTypes().forEach((pType) =>
		{
			const tmpActive = (pType.Key === pStyle);
			this.pict.ContentAssignment[tmpActive ? 'addClass' : 'removeClass'](`#ConfigNavSwitch-${pType.Key}`, 'is-link');
			this.pict.ContentAssignment[tmpActive ? 'addClass' : 'removeClass'](`#ConfigNavSwitch-${pType.Key}`, 'is-selected');
		});

		// Body: a welcome for the docked rail, or the (everything) page nav as a home screen.
		if (tmpProvider.isRailRenderType(pStyle))
		{
			this.pict.ContentAssignment.assignContent('#Navigation-Container', this._welcomeHTML());
		}
		else
		{
			tmpProvider.render(pStyle, '#Navigation-Container');
		}
	}

	_welcomeHTML()
	{
		return html`
			<div class="config-nav-welcome">
				<span class="config-nav-welcome-ic"><i class="fas fa-bars-staggered"></i></span>
				<h2 class="config-nav-welcome-title">Your navigation is docked on the left</h2>
				<p class="config-nav-welcome-sub">Every configuration area lives in the sidebar — search it or pick an area, and each entry explains what it does.</p>
				<div class="config-nav-welcome-keys">
					<span><kbd>⌘</kbd><kbd>K</kbd> &nbsp;jump to anything (or <kbd>Ctrl</kbd><kbd>K</kbd>)</span>
					<span><kbd>F1</kbd> &nbsp;open help</span>
				</div>
				<p class="config-nav-welcome-hint">Prefer a full-screen home? Choose <strong>Hub</strong>, <strong>List</strong>, or <strong>Command Palette</strong> above.</p>
			</div>`;
	}
}

module.exports = ConfigurationNavigation;

module.exports.default_configuration =
{
	ViewIdentifier: 'ConfigurationNavigation',
	DefaultRenderable: 'ConfigurationNavigationWrap',
	DefaultDestinationAddress: '#PlatformConfiguration-Application-Container',
	AutoRender: false,

	CSS: /*css*/`
/* Navigation-style chooser — a footer control: right-justified at the bottom of the page. */
.config-nav-switcher { display: flex; align-items: center; justify-content: flex-end; gap: 0.75rem; flex-wrap: wrap;
	margin-top: 1.75rem; padding-top: 1.1rem; border-top: 1px solid var(--bulma-border-weak, #e8ebf0); }
.config-nav-switcher .config-nav-switcher-label { font-size: 0.72rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.06em; color: var(--bulma-text-weak, #6b7686); }
.config-nav-switcher .buttons { margin-bottom: 0; }
.config-nav-switcher .buttons .button { margin-bottom: 0; gap: 0.4rem; }
.config-nav-switcher .buttons .button .icon { font-size: 0.85em; }

/* Welcome panel shown on the Configurations home when the docked Sidebar style is active. */
.config-nav-welcome { max-width: 620px; margin: 2rem auto; text-align: center; color: var(--bulma-text, #1f2733); }
.config-nav-welcome-ic { display: inline-flex; align-items: center; justify-content: center; width: 64px; height: 64px;
	border-radius: 16px; font-size: 1.7rem; margin-bottom: 1rem;
	background: var(--bulma-scheme-main-bis, #f4f6fa); color: var(--bulma-link, #156dd1); }
.config-nav-welcome-title { font-size: 1.4rem; font-weight: 700; margin: 0 0 0.4rem; }
.config-nav-welcome-sub { color: var(--bulma-text-weak, #6b7686); margin: 0 0 1.4rem; }
.config-nav-welcome-keys { display: flex; gap: 1.5rem; justify-content: center; flex-wrap: wrap; margin-bottom: 1.4rem;
	font-size: 0.85rem; color: var(--bulma-text-weak, #6b7686); }
.config-nav-welcome-keys kbd { background: var(--bulma-scheme-main-bis, #eceef2); border: 1px solid var(--bulma-border, #d7dce3);
	border-radius: 5px; padding: 1px 6px; font: inherit; font-weight: 700; margin: 0 1px; }
.config-nav-welcome-hint { font-size: 0.85rem; color: var(--bulma-text-weak, #6b7686); }
`,

	Templates:
	[
		libNavigationTopics.KPI_CARD_TEMPLATE,
		{
			Hash: 'ConfigurationNavigation-Template',
			Template: html`
				<section class="section">
					<div class="container">
						<div class="nav-jumbo">
							<div class="nav-jumbo-head">
								<span class="nav-jumbo-ic"><i class="{~D:AppData.NavJumbo.Icon~}"></i></span>
								<div>
									<h1 class="nav-jumbo-title">{~D:AppData.NavJumbo.Title~}</h1>
									<p class="nav-jumbo-sub">{~D:AppData.NavJumbo.Blurb~}</p>
								</div>
							</div>
							<div class="nav-kpi-strip">{~TS:Nav-Kpi-Card:AppData.NavKpis~}</div>
						</div>
						<div id="Navigation-Container" class="pict-nav"></div>
						<div id="ConfigNav-Switcher" class="config-nav-switcher"></div>
					</div>
				</section>`,
		},
	],
	Renderables:
	[
		{
			RenderableHash: 'ConfigurationNavigationWrap',
			TemplateHash: 'ConfigurationNavigation-Template',
			DestinationAddress: '#PlatformConfiguration-Application-Container',
		},
	],
};
